import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Modal from '../components/Modal';
import { getAuctioneerListCompact } from '../lib/api/admin/adminApi';
import { AuctioneerCompactData } from '../lib/api/admin/adminApi.types';
import { devError, devLog } from '../utils/devLogger';

// 경매사 데이터 타입 정의
export interface AuctioneerSearchItem {
  index: number;
  no: number;
  name: string;
}

// 팝업 props 타입 정의
interface AuctioneerSearchPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (auctioneer: AuctioneerSearchItem) => void;
  selectedAuctioneer?: AuctioneerSearchItem | null;
  title?: string;
}

const AuctioneerSearchPopup: React.FC<AuctioneerSearchPopupProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedAuctioneer,
  title
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<AuctioneerSearchItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // 페이지네이션 계산
  const totalPages = Math.ceil(searchResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = searchResults.slice(startIndex, endIndex);

  // 검색 함수
  const handleSearch = useCallback(async (searchQuery: string = searchTerm) => {
    setIsLoading(true);
    try {
      // searchQuery가 빈 문자열이어도 API 호출 (모든 경매사 조회)
      const response = await getAuctioneerListCompact(searchQuery);
      devLog('경매사 검색 응답:', response);
      
      // API 응답 구조에 맞게 데이터 추출
      let auctioneers: AuctioneerCompactData[] = [];
      if (response?.data?.data && Array.isArray(response.data.data)) {
        auctioneers = response.data.data;
      } else if (response?.data && Array.isArray(response.data)) {
        auctioneers = response.data;
      }

      const searchItems: AuctioneerSearchItem[] = auctioneers.map(auctioneer => ({
        index: auctioneer.index,
        no: auctioneer.no,
        name: auctioneer.name
      }));

      setSearchResults(searchItems);
      setCurrentPage(1); // 검색 시 첫 페이지로 이동
    } catch (error) {
      devError('경매사 검색 중 오류:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm]);

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 팝업이 열릴 때 초기화 및 모든 경매사 조회
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setCurrentPage(1);
      // 팝업이 열리면 즉시 모든 경매사를 조회 (name 파라미터 없이)
      handleSearch('');
    }
  }, [isOpen, handleSearch]);

  const modalContent = (
    <PopupContainer>
      {/* 검색 영역 */}
      <SearchSection>
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="경매사명을 입력하세요"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
          <SearchButtonStyled onClick={() => handleSearch()}>
            조회
          </SearchButtonStyled>
        </SearchContainer>
      </SearchSection>

      {/* 결과 정보 및 페이지네이션 */}
      <ControlSection>
        {searchResults.length > 0 && (
          <PaginationControlsStyled>
            <InfoText>
              전체 {searchResults.length}건 중 {currentItems.length}건
            </InfoText>
            <NavButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1 || isLoading}
            >
              &lt;
            </NavButton>
            <PageBox>
              {currentPage} / {totalPages > 0 ? totalPages : 1}
            </PageBox>
            <NavButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || isLoading}
            >
              &gt;
            </NavButton>
            <ItemsPerPageSelect
              value={itemsPerPage}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const newSize = parseInt(e.target.value);
                setItemsPerPage(newSize);
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </ItemsPerPageSelect>
            <InfoText style={{ marginLeft: '5px' }}>
              개씩 보기
            </InfoText>
          </PaginationControlsStyled>
        )}
      </ControlSection>

      {/* 테이블 영역 */}
      <TableSection>
        {isLoading ? (
          <LoadingMessage>검색 중...</LoadingMessage>
        ) : searchResults.length === 0 ? (
          <NoResultsMessage>검색 결과가 없습니다</NoResultsMessage>
        ) : (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell style={{ width: '80px', textAlign: 'center' }}>No</TableHeaderCell>
                  <TableHeaderCell style={{ width: '120px', textAlign: 'center' }}>경매사 ID</TableHeaderCell>
                  <TableHeaderCell style={{ textAlign: 'center' }}>경매사명</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentItems.map((item, index) => (
                  <TableRow
                    key={item.index}
                    onClick={() => {
                      onSelect(item);
                      onClose();
                    }}
                    $hoverable
                  >
                    <TableCell style={{ textAlign: 'center' }}>{startIndex + index + 1}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>{item.no}</TableCell>
                    <TableCell>{item.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TableSection>
    </PopupContainer>
  );

  const modalFooter = null; // 하단 버튼 제거

  const modalHeader = (
    <HeaderContainer>
      <HeaderTitle>경매사 조회</HeaderTitle>
      <CloseButton onClick={onClose}>
        ✕
      </CloseButton>
    </HeaderContainer>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header=""
      body={
        <>
          {modalHeader}
          {modalContent}
        </>
      }
      footer={modalFooter}
      width="800px"
      height="600px"
      zIndex={10100}
      padding="0"
      borderRadius="0"
    />
  );
};

export default AuctioneerSearchPopup;

// Styled Components
const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  background-color: #ffffff;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const PopupContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 500px;
  background-color: #ffffff;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const SearchSection = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const SearchInput = styled.input`
  width: 250px;
  height: 40px;
  border: 1px solid #e0e0e0;
  border-right: none;
  color: #333;
  padding-left: 15px;
  padding-right: 35px;
  background-color: #ffffff;
  font-size: 14px;
  box-sizing: border-box;

  &::placeholder {
    color: #c4c5c9;
  }

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const SearchButtonStyled = styled.button`
  width: 60px;
  height: 40px;
  background: #214a72;
  border: 1px solid #214a72;
  color: #fff;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  box-sizing: border-box;
  margin: 0;
  padding: 0;

  &:hover {
    opacity: 0.9;
  }
`;

const ControlSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 12px 24px;
  background-color: transparent;
  border-bottom: 1px solid #eee;
`;

const PaginationControlsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoText = styled.span`
  margin: 0;
  font-size: 14px;
  color: #887e67;
  white-space: nowrap;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  background-color: #ddd8c7;
  color: #333333;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  transition: background-color 0.2s, border-color 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.8;
    border-color: #999;
    background-color: #f8f8f8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #EEEEEE;
    color: #AAAAAA;
  }
`;

const PageBox = styled.div`
  margin: 0 5px;
  font-size: 14px;
  color: #887e67;
  white-space: nowrap;
`;

const ItemsPerPageSelect = styled.select`
  width: 60px;
  height: 30px;
  padding: 4px 8px;
  border: 1px solid #e0e0e0;
  background-color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  text-align: center;
  color: #333333;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  option {
    background-color: #ffffff;
    color: #333;
  }
`;

const TableSection = styled.div`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 300px;
  padding: 0;
  box-sizing: border-box;
`;

const LoadingMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #666;
  font-size: 16px;
  border: 1px solid #e0e0e0;
  background: #ffffff;
  margin: 0 24px 24px 24px;
`;

const NoResultsMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #999;
  font-size: 16px;
  border: 1px solid #e0e0e0;
  background: #ffffff;
  margin: 0 24px 24px 24px;
`;

const TableContainer = styled.div`
  width: calc(100% - 48px);
  border: 1px solid #e0e0e0;
  background: #ffffff;
  overflow: hidden;
  margin: 0 24px 24px 24px;
  box-sizing: border-box;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  box-sizing: border-box;
`;

const TableHeader = styled.thead`
  background-color: #f8f8f8;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $selected?: boolean; $hoverable?: boolean }>`
  cursor: ${({ $hoverable }) => $hoverable ? 'pointer' : 'default'};
  transition: background-color 0.2s;
  background-color: ${({ $selected }) => $selected ? '#e3f2fd' : 'transparent'};
  border-bottom: 1px solid #f0f0f0;
  
  &:hover {
    background-color: ${({ $hoverable }) => $hoverable ? '#f8f8f8' : 'transparent'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #333333;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
  background-color: #f8f8f8;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const TableCell = styled.td`
  padding: 15px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #333333;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;